// VFR Devis Digital - Type Definitions

// ============================================
// User & Authentication Types
// ============================================

export type UserRole = 'admin' | 'standard' | 'guest'

export interface User {
  id: string
  email: string
  name: string
  role: UserRole
}

export interface Session {
  id: string
  userId: string
  expiresAt: number
  createdAt: number
}

export interface LoginRequest {
  email: string
  pin: string
}

export interface LoginResponse {
  sessionId: string
  user: User
  role: UserRole
}

// Auth constants
export const SESSION_TIMEOUT_MS = 3600000 // 1 hour
export const SESSION_REFRESH_THRESHOLD_MS = 300000 // 5 minutes before expiration

// ============================================
// Client Types
// ============================================

export interface Client {
  id: string
  name: string
  siret: string
  email?: string
  phone?: string
  address?: string  // street + number (Dolibarr `address`)
  zip?: string      // postal code (Dolibarr `zip`)
  town?: string     // city (Dolibarr `town`)
  freshId?: string
  createdAt: number
  updatedAt: number
}

export interface DolibarrContact {
  id: string
  lastname: string
  firstname: string
  poste?: string
  email?: string
  phone_pro?: string
  phone_mobile?: string
}

// ============================================
// Product Types
// ============================================

export type ProductCategory =
  | 'textile'
  | 'grand_format'
  | 'vehicule'
  | 'impression'
  | 'objets_pub'

export interface ProductCategoryConfig {
  id: ProductCategory
  label: string
  description: string
  icon: string
  color: string
}

export const PRODUCT_CATEGORIES: ProductCategoryConfig[] = [
  {
    id: 'textile',
    label: 'Textile',
    description: 'T-shirts, polos, vêtements personnalisés',
    icon: 'tshirt',
    color: 'blue'
  },
  {
    id: 'grand_format',
    label: 'Grand Format',
    description: 'Bâches, banderoles, kakémonos',
    icon: 'image',
    color: 'green'
  },
  {
    id: 'vehicule',
    label: 'Véhicule',
    description: 'Covering, marquage véhicule',
    icon: 'car',
    color: 'orange'
  },
  {
    id: 'impression',
    label: 'Impression',
    description: 'Cartes de visite, flyers, affiches',
    icon: 'printer',
    color: 'purple'
  },
  {
    id: 'objets_pub',
    label: 'Objets Pub',
    description: 'Goodies, objets promotionnels',
    icon: 'gift',
    color: 'pink'
  }
]

// ============================================
// Supplier Catalog Types
// ============================================

export type CatalogType = 'pdf' | 'web'

export interface SupplierCatalog {
  id: string
  name: string
  type: CatalogType
  url: string
  category: ProductCategory
}

export const SUPPLIER_CATALOGS: SupplierCatalog[] = [
  // Textile
  {
    id: 'sols-2024',
    name: "SOL'S 2024",
    type: 'pdf',
    url: 'https://www.sols-europe.com/catalogue',
    category: 'textile'
  },
  {
    id: 'fruit-of-the-loom',
    name: 'Fruit of the Loom',
    type: 'web',
    url: 'https://www.fruitoftheloom.eu/catalogue',
    category: 'textile'
  },
  {
    id: 'stanley-stella',
    name: 'Stanley/Stella Bio',
    type: 'pdf',
    url: 'https://www.stanleystella.com/catalogue',
    category: 'textile'
  },
  // Grand Format
  {
    id: 'orajet-grand-format',
    name: 'Orajet Adhésifs',
    type: 'pdf',
    url: 'https://www.orafol.com/orajet',
    category: 'grand_format'
  },
  {
    id: 'hexis-films',
    name: 'Hexis Films',
    type: 'web',
    url: 'https://www.hexis-graphics.com/catalogue',
    category: 'grand_format'
  },
  // Vehicule
  {
    id: 'orajet-vehicule',
    name: 'Orajet Véhicule',
    type: 'pdf',
    url: 'https://www.orafol.com/orajet-vehicle',
    category: 'vehicule'
  },
  {
    id: 'hexis-vehicule',
    name: 'Hexis Covering',
    type: 'web',
    url: 'https://www.hexis-graphics.com/vehicle-wrapping',
    category: 'vehicule'
  },
  {
    id: '3m-vehicule',
    name: '3M Wrap Films',
    type: 'pdf',
    url: 'https://www.3m.com/wrap-films',
    category: 'vehicule'
  },
  // Impression
  {
    id: 'papiers-cotes',
    name: 'Papiers Couchés',
    type: 'pdf',
    url: 'https://example.com/papiers-couches',
    category: 'impression'
  },
  {
    id: 'supports-rigides',
    name: 'Supports Rigides',
    type: 'pdf',
    url: 'https://example.com/supports-rigides',
    category: 'impression'
  },
  // Objets Pub
  {
    id: 'topgiving',
    name: 'Topgiving',
    type: 'web',
    url: 'https://www.topgiving.fr/catalogue',
    category: 'objets_pub'
  },
  {
    id: 'midocean',
    name: 'Midocean',
    type: 'web',
    url: 'https://www.midocean.com/catalogue',
    category: 'objets_pub'
  }
]

export function getCatalogsByCategory(category: ProductCategory): SupplierCatalog[] {
  return SUPPLIER_CATALOGS.filter(catalog => catalog.category === category)
}

// ============================================
// Quote Types
// ============================================

export type QuoteStatus =
  | 'brouillon'
  | 'attente_validation'
  | 'valide_local'
  | 'synchronise'
  | 'rejete'
  | 'signature_pending'
  | 'signe'
  // Note mode: dossier priced by hand in Dolibarr, never synced
  | 'note_a_chiffrer'
  | 'note_chiffree'
  | 'note_abandonnee'

export type NoteStatus = 'note_a_chiffrer' | 'note_chiffree' | 'note_abandonnee'

export interface QuoteItem {
  id: string
  productId: string
  category: string
  description: string
  quantity: number
  unitPrice: number
  totalPrice: number
  details?: Record<string, unknown>
}

export interface Quote {
  id: string
  clientId: string
  client?: Client
  contactId?: string
  contactName?: string
  status: QuoteStatus
  items: QuoteItem[]
  totalHT: number
  totalTTC: number
  notes?: string
  internalNotes?: string
  showDelivery?: boolean
  urgency?: 'standard' | 'urgent' | 'express'
  deliveryDate?: string
  createdBy: string
  createdAt: number
  updatedAt: number
  syncedAt?: number
  freshId?: string
  freshRef?: string
  // Story 6-5: Sync failure tracking
  syncFailed?: boolean
  syncFailedAt?: number
  syncErrorMessage?: string
  // Note mode: Dolibarr proposal ref entered by the admin once priced
  noteRef?: string
}

// ============================================
// API Response Types
// ============================================

export interface ApiSuccessResponse<T> {
  data: T
  meta?: {
    total?: number
    page?: number
  }
}

export interface ApiErrorResponse {
  error: true
  code: string
  message: string
}

export type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse

// ============================================
// Sync Types
// ============================================

export type SyncStatus = 'pending' | 'syncing' | 'success' | 'failed'

export interface SyncQueueItem {
  id: string
  quoteId: string
  status: SyncStatus
  retryCount: number
  lastError?: string
  createdAt: number
  updatedAt: number
}

// ============================================
// UI Types
// ============================================

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface ToastAction {
  label: string
  handler: () => void
  variant?: 'primary' | 'secondary'
}

export interface Toast {
  id: string
  type: ToastType
  title?: string
  message: string
  duration?: number
  actions?: ToastAction[]
}
